/**
 * React Query invalidation map for DGPT agent writes to ICS data.
 *
 * Single source of truth shared by the websocket `dchat_ics_update` handler
 * (per-write, near-realtime) and the agent-run-completion sweep in
 * ChatDisasterGPT (belt-and-braces for dropped frames / unmapped writes). Keys
 * must match the `base` of the corresponding IAP hooks — e.g.
 * incidentObjectiveHooks, useSafetyAnalysis (208HM), incidentRoster.queryKeys.
 */
import type { QueryClient } from '@tanstack/react-query';

/**
 * Base query keys per ICS entity name (the generic create/update_ics_record
 * tools) or per tool name (writes that aren't entity-scoped). Entity names
 * mirror COMS DGPT/tools/ics/entityDescriptors.ts ICSEntityName.
 */
export const icsUpdateQueryKeys: Record<string, string[]> = {
  // by entity
  objective: ['incident_objectives'],
  action: ['incident_actions'],
  resource: ['resource_sets', 'resources-in-incident'],
  resource_in_incident: ['resources-in-incident', 'resource_sets'],
  org_assignment: ['icsIncidentOrganizationAssignments'],
  safety_message: ['safetyMessages'],
  // ICS-208HM reads both the hazmat record and incident info (weather/PPE)
  // through the useSafetyAnalysis keys ('hazmats' / 'incidentInfo').
  hazmat: ['hazmats', 'incidentInfo'],
  // ICS-206 medical plan (incidentHospitalsHooks / incidentMedicalAidStationsHooks /
  // incidentMedicalTransportationHooks `keys.base`).
  hospital: ['incident_hospitals'],
  medical_aid_station: ['incident_medicalAidStations'],
  medical_transportation: ['incident_medicalTransportation'],
  // ICS-205 radio communications plan (incidentComsHooks `keys.base`).
  communication: ['incident_coms'],
  // ICS-204 assignment list cards (assignmentListHooks `keys.base`).
  assignment_list: ['incident_workAssignmentListAttachment'],
  // by tool (no entity field). incident_info also backs the ICS-205 "Special
  // Instructions" text (communicationsSpecialInstructions).
  update_incident_info: [
    'incident_info',
    'incidentInfo',
    'incidentStatusSummary',
  ],
  // Roster seats own their Personnel resources (created/deleted with the
  // assignment), so seat writes refresh the resource lists and the org chart's
  // roster resources too.
  assign_roster_position: [
    'incidentRoster',
    'rosterResources',
    'resource_sets',
    'resources-in-incident',
  ],
  remove_roster_position: [
    'incidentRoster',
    'rosterResources',
    'resource_sets',
    'resources-in-incident',
  ],
};

/**
 * Union of every ICS base the agent can touch — the fallback for unmapped
 * writes and the run-completion sweep. Over-invalidation is cheap:
 * invalidateQueries only refetches queries that are currently MOUNTED.
 */
export const icsAllQueryKeyBases: string[] = [
  ...new Set(Object.values(icsUpdateQueryKeys).flat()),
];

export const invalidateIcsQueries = (
  queryClient: QueryClient,
  bases: string[]
): void => {
  bases.forEach((base) => {
    queryClient.invalidateQueries({ queryKey: [base] });
  });
};
